import apiClient from "../../services/apiClient";
import { trackEvent } from "../analytics/analytics";

/**
 * S98 yearly offer: one line, once, for active monthly Stripe payers in months 2-12.
 *
 * The server decides who sees it (`YearlyOffer` / `YearlyOfferService`) and spends the account's one
 * showing on the first enrolled answer. The client only asks once per tab session and holds the answer
 * in sessionStorage, so a fast navigation or reload does not lose the only showing, and a free account
 * never asks at all.
 */

export const YEARLY_OFFER = "yearly-offer";

/** "none", or the plan code the CTA links to. */
const SESSION_KEY = "yearly_offer";
/** Set on CTA click so the plan page can log a buy; expires so a stale click cannot fire later. */
const CLICKED_KEY = "yearly_offer_clicked";
const CLICKED_TTL_MS = 60 * 60 * 1000;

interface YearlyOfferResponse {
  enrolled?: boolean;
  plan?: string | null;
}

const read = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null) => {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage blocked: the line may be asked for again, and the server answers no.
  }
};

/** The plan code to offer yearly on, or null. Asks the server at most once per tab session. */
export const loadYearlyOffer = async (): Promise<string | null> => {
  const cached = read(SESSION_KEY);
  if (cached) return cached === "none" ? null : cached;

  const { data } = await apiClient.get<YearlyOfferResponse>(
    `/account/experiment/${YEARLY_OFFER}`,
  );
  const plan = data.enrolled && data.plan ? data.plan : null;
  write(SESSION_KEY, plan ?? "none");
  return plan;
};

const recordRoute = (route: "dismiss" | "click") =>
  apiClient.post(`/account/experiment/${YEARLY_OFFER}/route`, { route });

/** Gone for good: the server writes the dismissal on the account. */
export const dismissYearlyOffer = async () => {
  write(SESSION_KEY, "none");
  await recordRoute("dismiss");
};

/** Logs the click and hides the line for the session; the caller navigates to the plan page. */
export const clickYearlyOffer = async () => {
  write(SESSION_KEY, "none");
  write(CLICKED_KEY, String(Date.now()));
  await recordRoute("click");
};

/**
 * Called by the plan page when a change to yearly commits. A convenience event only: the buy of record
 * is the `change plan` Actions row with payPeriod 12 after a `show`.
 */
export const trackYearlyOfferBuy = (planCode: string) => {
  const clicked = Number(read(CLICKED_KEY));
  write(CLICKED_KEY, null);
  if (!clicked || Date.now() - clicked > CLICKED_TTL_MS) return;
  trackEvent({
    type: "conversion",
    category: YEARLY_OFFER,
    action: "buy",
    label: planCode,
  });
};
